<template>
  <div class="docs-content">
    <div v-if="canEdit">
      <template v-if="video.length">
        <p class="text-muted">
          Video
        </p>

        <ul class="files clearfix">
          <document
            v-for="document in video"
            :show-icon="!document.link"
            :key="document.url"
            :document="document"
            :startup-id="startup.id"
            hide-allow-download
            class="file-pdf"
          />
        </ul>
      </template>

      <div
        v-if="!video.length"
        class="flex flex-col items-center justify-center space-y-6"
      >
        <button
          class="btn btn-primary btn-sm"
          @click="uploadDocument({ isVideo: true })"
          :disabled="uploadInProgress"
        >
          Upload video
        </button>

        <span class="text-muted">or</span>

        <el-form
          :model="videoForm"
          ref="videoForm"
          :rules="videoFormRules"
        >
          <el-form-item prop="videoUrl">
            <div class="w-80 flex items-center flex-1 space-x-2">
              <el-input
                v-model="videoForm.videoUrl"
                class="flex-1"
                placeholder="Use video URL (eg. YouTube or Vimeo)"
                size="small"
                type="url"
              />
              <button
                :disabled="uploadInProgress"
                class="btn btn-primary btn-sm"
                @click.prevent="submitForm('videoForm')"
              >
                Save URL
              </button>
            </div>
          </el-form-item>
        </el-form>
      </div>

      <transition leave-active-class="animated fadeOut">
        <div class="progress margin-top-30" v-show="uploadInProgress">
          <vb-progressbar
            label="File upload"
            :now="progress"
            type="success"
          />
        </div>
      </transition>

      <input
        ref="fileInput"
        type="file"
        @change="
          onFilesChosen({
            filename: $event.target.name,
            file: $event.target.files[0],
            event: $event,
          })
        "
        class="hidden"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import http from 'services/http'
import _ from 'lodash'
import { mapState } from 'vuex'
import draggable from 'vuedraggable'
import mime from 'mime-types'
import TextButton from 'components/common/TextButton.vue'
import helpMessages from './helpMessages'
import Document from './Document'

export default {
  components: { Document, draggable, TextButton },
  props: ['startup', 'editable'],
  data() {
    return {
      helpMessages,
      uploadInProgress: false,
      isRemoveInProgress: false,
      progress: 0,
      goingToUploadVideo: undefined,
      tmpVideoUrl: null,
      videoForm: {
        videoUrl: null,
      },
      videoFormRules: {
        videoUrl: [
          {
            required: false,
            pattern: /(https?:\/\/)www.(youtube.com\/watch[?]v=([a-zA-Z0-9_-]{11}))|https?:\/\/(www.)?vimeo.com\/([0-9]{9})/gi,
            message: 'This has to be a valid URL (including https / http)',
            trigger: 'blur',
          },
        ],
      },
    }
  },
  mounted() {
    this.unwatchStartup = this.$watch(
      'startup',
      (val) => {
        if (val.video_url) {
          this.videoForm = {
            ...this.videoForm,
            videoUrl: val.video_url,
          }
          this.unwatchStartup()
        }
      },
      { deep: true },
    )
  },
  computed: {
    documents: {
      get() {
        return this.startup.documents
      },
      async set(documents) {
        this.$store.commit('startupProfile/SET_STARTUP_ATTR', {
          attr: 'documents',
          value: documents,
        })
        await this.$store.dispatch('startupProfile/reorderDocuments', {
          documentsIds: this.documents.map((d) => d.id),
        })
      },
    },
    video() {
      if (this.startup.video_url) {
        return [
          {
            filename: this.startup.video_url,
            url: this.startup.video_url,
            link: true,
          },
        ]
      }
      return this.documents.filter((d) => d.is_video)
    },
    ...mapState({
      user: (state) => state.user.data,
      canEdit(state) {
        const isAdmin = state.user.data.isAdmin
        const startups = state.user.data.startups || []
        const startupIds = new Set(startups.map((s) => s.id))
        const isOwner = startupIds.has(this.startup.id)
        return this.editable && (isAdmin || isOwner)
      },
    }),
  },
  methods: {
    uploadDocument({ isVideo = false } = {}) {
      this.goingToUploadVideo = isVideo
      this.$refs.fileInput.click()
    },
    async onFilesChosen({ filename, file, event }) {
      if (!file) {
        return
      }

      const isVideo = this.goingToUploadVideo
      this.goingToUploadVideo = undefined

      // Check file size
      if (file.size >= 20971520) {
        this.$message.error({
          message: 'File cannot exceed 20 MB in size!',
          duration: 5000,
        })
        return
      }

      const allowedVideoExtensions = ['mp4', 'mkv', 'avi', 'mov']
      const fileType = () => {
        if (file.type === 'video/avi') return 'video/x-msvideo'
        return file.type
      }
      const ext = () => {
        if (fileType() === 'video/quicktime') {
          return 'mov'
        }
        return mime.extension(fileType())
      }
      if (isVideo && !allowedVideoExtensions.includes(ext())) {
        this.$message.error(
          `Only videos with these extensions can be uploaded: ${allowedVideoExtensions.join(
            ', ',
          )}`,
        )
        this.$refs.fileInput.value = null
        return
      }
      await this.cleanVideoUrl()
      const self = this
      self.uploadInProgress = true

      const response = await http.postAuthed(
        `/startups/${this.startup.id}/document-upload-data`,
        {
          startup_id: this.startup.id,
          filename: file.name,
          content_type: file.type,
        },
      )
      const key = response.data.fields.key
      const documentUrl = response.data.url + key

      const data = new FormData()
      _.toPairs(response.data.fields).forEach((pair) => {
        data.append(pair[0], pair[1])
      })
      data.append('file', file)
      const uploadResponse = await axios.post(response.data.url, data, {
        onUploadProgress(progressEvent) {
          self.progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          )
        },
      })

      const document = {
        startup_id: self.startup.id,
        filename: file.name,
        key: key,
        url: documentUrl,
        size: file.size,
        content_type: file.type,
        is_pitch: false,
        is_video: isVideo,
      }
      const notifyResponse = await http.postAuthed(
        `/startups/${this.startup.id}/document-uploaded`,
        document,
      )
      self.$store.commit('startupProfile/ADD_DOCUMENT', notifyResponse.data)

      setTimeout(() => {
        self.uploadInProgress = false
        self.progress = 0
        self.$refs.fileInput.value = null
      }, 700)
    },
    async openRemoveModal(document) {
      try {
        await this.$confirm(
          `Are you sure you want to delete ${document.link ? '' : 'document'} ${
            document.filename
          }?`,
          'Confirm deletion',
        )
      } catch (err) {
        // removal canceled
        return
      }
      return await this.remove({ document })
    },
    async remove({ document }) {
      this.isRemoveInProgress = true
      await this.cleanVideoUrl()
      if (!document.link) {
        await this.$store.dispatch('startupProfile/removeDocument', {
          document,
        })
      }
      this.isRemoveInProgress = false
      this.$message({
        type: 'success',
        message: `${document.link ? 'Link' : 'Document'} removed`,
      })
    },
    async openDocument(document) {
      // This is here to fix expiring S3 links for Pitch Deck files. They expire by default in 1 hour
      // (this only makes sense when not editigin - at editor it might happen in the middle of adding a team member)
      await this.$store.dispatch('startupProfile/fetch', {
        id: this.startup.id,
        delay: 750,
      })
      // XXX: this code assumes that clicked document is not removed at the backend
      const newDocument = this.startup.documents.filter((d) => {
        return d.id === document.id
      })[0]
      window.open(newDocument.url, '_new').focus()
    },
    async cleanVideoUrl() {
      await this.cleanForm()
      this.$store.commit('startupProfile/SET_STARTUP_ATTR', {
        attr: 'video_url',
        value: null,
      })
    },
    cleanForm() {
      this.videoForm = {
        videoUrl: null,
      }
    },
    submitForm(formName) {
      this.$refs[formName].validate((valid) => {
        if (valid) {
          this.$store.commit('startupProfile/SET_STARTUP_ATTR', {
            attr: 'video_url',
            value: this.videoForm.videoUrl,
          })
        } else {
          return false
        }
      })
    },
  },
}
</script>

<style lang="scss">
@import '~assets/css/colors.scss';

.docs-content {
  padding: 18px 17px;

  .files {
    .file-pdf {
      &.sortable-ghost {
        background-color: $grey160;
      }
    }
  }
}

.section-header {
  margin: 24px 0 30px;
}
</style>
